import { RxGraphQLReplicationQueryBuilder, RxJsonSchema, SyncOptionsGraphQL } from "rxdb";
import { RxGraphQLReplicationState } from "rxdb/dist/types/plugins/replication-graphql";
import { SubscriptionClient } from "subscriptions-transport-ws";
import { AppState } from "../../app-state";

import { AuthenticationService } from "..";
import { Observable } from "rxjs";
import { filter, map, takeUntil, tap } from "rxjs/operators";
import { TestbedRxdbCollectionService } from "./testbed-rxdb-collection.service";
import { TestbedRxdbState } from "./testbed-rxdb-state";
import { TestbedRxdbGraphQlSyncError } from "./testbed-rxdb-error";

export class TestbedRxdbGraphQlCollectionService<ModelType,RxDocType> 
    extends TestbedRxdbCollectionService<ModelType,RxDocType> {
    
    static wsClient : SubscriptionClient

    static initWebSocketInstance(graphQlWebSocketUrl:string) {
        TestbedRxdbGraphQlCollectionService.wsClient = new SubscriptionClient(
            graphQlWebSocketUrl, 
            {
                reconnect: true,
                timeout: 1000 * 60,
                // connectionCallback: () => {
                //     console.log('SubscriptionClient.connectionCallback:');
                // },
                reconnectionAttempts: 10000,
                inactivityTimeout: 10 * 1000,
                lazy: true
            }
       );
    }

    graphQLSubscriptionObservable?: any


    constructor( 
            collectionName : string,
            schema: RxJsonSchema,
            appState: AppState,
            protected rxdbState: TestbedRxdbState,
            protected authService : AuthenticationService ) {
        super(collectionName, schema, appState, rxdbState)
        this.error$.pipe( map( e => this.wrapSyncError(e) ) ).subscribe( e => {
            if ( e.error ) {
                //console.dir(e.error)
                if ( !this.lastError?.equals(e) ) {
                    this.appState.publishErrorObj( e.extensionsEx || e.error, 
                        `${this.name} sync error. ${e.extractReason()}`, this)
                }
            }
            this.lastError = e
        })
    }

    lastError : TestbedRxdbGraphQlSyncError

    buildSyncOptions() : SyncOptionsGraphQL {
        return {
            url: `${this.appState.settings.getGraphQLUrl()}`,
            headers: { Authorization: 'Bearer ' + this.authService.currentUserValue.token },
            waitForLeadership: true,
            pull: {
                queryBuilder: this.syncConfig.query as RxGraphQLReplicationQueryBuilder,
            },
            push: {
                queryBuilder: this.syncConfig.pushQuery as RxGraphQLReplicationQueryBuilder
            },
            deletedFlag: 'deleted',
            live:  this.syncConfig.intervalMs == 0,
            liveInterval: 60 * 1000,
            syncRevisions: false  //VBTMA-226 RxDB data sync issues after One source import if syncRevisions=true
        }
    }


    async startSync() {


        
        if ( !this.syncConfig?.name ) {
            this.syncConfig.name = this.name
        }

        this.httpStatusCode = 200 // default to ok because we don't check the http responses for graphql

        const isLive = this.syncConfig.intervalMs == 0

        this.bStopped = false
        if ( isLive ) {
            console.log('Starting live GraphQL sync ' + this.name)
            this.doLiveSync()
        } else {
            console.log('Starting nonLive GraphQL sync ' + this.name + ' every ' + (this.syncConfig.intervalMs/1000.0).toFixed(2) + ' second(s)')
            this.doNonLiveSync()
        }
    }


    async doLiveSync() {
        let syncOpts = this.buildSyncOptions()
        var localSyncState = {
            okCntBeforeSync : 0,
            error: null
        }

        if ( this.replicationState ) {
            await this.replicationState.cancel()
            this.replicationStateListeners?.forEach(listener=>listener?.replicationStateCanceled(this.replicationState))            
        }
        this.replicationState = this.rxCollection.syncGraphQL( syncOpts )
        this.replicationStateListeners?.forEach(listener=>listener?.replicationStateCreated(this.replicationState))

        this.graphQLSubscriptionObservable = TestbedRxdbGraphQlCollectionService.wsClient.request({ 
            query: this.syncConfig.graphQLSubscription 
        });
        
        this.graphQLSubscriptionObservable.subscribe({
            next: async (data) => {
                // console.log(data);
                await (this.replicationState as RxGraphQLReplicationState).run()
            },
            error(error) {
                console.log('run() got error:')
                console.dir(error)
            }
        })

        this.replicationState.initialReplicationComplete$.pipe(
            takeUntil(this.replicationStateStale)
        ).subscribe( bComplete => {             
            console.log(`${this.name} live sync initialReplicationComplete: ${bComplete}`)
            this.initialReplicationComplete$.next(bComplete)
            if ( bComplete ) {
                this.replicationStateStale.complete()
            }
            this.rxdbState.processSyncComplete(this,bComplete);
        })
        

        this.replicationState.active$.subscribe( bActive => {
            //console.log(`${this.name} live sync bActive: ${bActive}`)
            this.replicationActive$.emit(bActive)
            this.bActiveSync = bActive;
            this.rxdbState.processSyncActive(this,bActive)
            if ( bActive ) {
                localSyncState.okCntBeforeSync = this.rxdbState.getOkCount()
                localSyncState.error = null
            } else {
                if ( localSyncState.error ) {
                    this.rxdbState.processSyncComplete(this,false);
                    localSyncState.error = null
                } else {
                    this.rxdbState.processSyncComplete(this,true); 
                    this.httpStatusCode = 200
                    this.updateRxdbStateAfterHttpFetch(localSyncState.okCntBeforeSync)
                    this.error$.emit(null)
                }
            }
        })
        
        this.replicationState.error$.subscribe( e => {
            console.error( 'Live sync "' + this.name + '":' + e)
            if ( e?.innerErrors ) {
                e.innerErrors.forEach( innerError => {
                    console.error(innerError.message)
                    console.dir(innerError)
                })
            } else {
                console.dir(e)
            }
            localSyncState.error = e
            this.error$.emit(e)
            this.httpStatusCode = 500 //TODO - e.innerErrors.extensions has error context
            this.updateRxdbStateAfterHttpFetch(localSyncState.okCntBeforeSync)
    })
    }


    async doNonLiveSync() {
        
        try {
            //console.log('doNonLiveSync(): ' + meta.rxCollection.name)
            var complete$ : Observable<any> = null;
            var active$ : Observable<any|boolean> = null;
            const syncOpts = this.buildSyncOptions()
            var localSyncState = {
                okCntBeforeSync : 0
            }

            this.replicationState = this.rxCollection?.syncGraphQL(syncOpts)
            if (!this.replicationState)
                return
            complete$ = this.replicationState.initialReplicationComplete$
            active$ = this.replicationState.active$.pipe(takeUntil(this.replicationStateStale))
            
            this.replicationStateListeners?.forEach(listener=>{
                listener?.replicationStateCreated(this.replicationState)
            });
            
            active$.subscribe( bActive => { 
                //console.log(`${this.name} sync bActive: ${bActive}`)
                this.replicationActive$.emit(bActive)
                if ( bActive ) {
                    localSyncState.okCntBeforeSync = this.rxdbState.getOkCount()
                }
                this.bActiveSync = bActive;                 
                this.rxdbState.processSyncActive(this,bActive); 
            })

            complete$.pipe(takeUntil(this.replicationStateStale)).subscribe( bComplete => { 
                //console.log(`${this.name} sync bComplete: ${bComplete}`)
                this.bActiveSync = false
                if ( bComplete && !this.initialReplicationComplete$.value ) {
                    this.initialReplicationComplete$.next(bComplete)
                }
                this.rxdbState.processSyncComplete(this,bComplete); 
                if ( bComplete ) {
                    this.httpStatusCode = 200
                    this.error$.emit(null)
                    this.scheduleNonLiveSync(this.syncConfig.intervalMs)
                }
            })
            
            this.replicationState.error$.pipe(takeUntil(this.replicationStateStale)).subscribe( e => { 
                //console.log(`${this.name} sync error`);  
                console.dir(e)
                e?.innerErrors?.forEach( innerError => {
                    console.error(innerError.message)
                    console.dir(innerError)
                })
                this.error$.emit(e)
                this.bActiveSync = false
                this.rxdbState.processSyncActive(this,false);
                this.httpStatusCode = 500 //TODO - e.innerErrors.extensions has error context
                this.updateRxdbStateAfterHttpFetch(localSyncState.okCntBeforeSync)
                const retryDelayMs = this.rxdbState.nextRetryDelayMs()
                console.log( 'next retry interval: ' + retryDelayMs)
                this.scheduleNonLiveSync(retryDelayMs)
                
            })
            
        } catch ( error ) {
            console.error('doNonLiveSync() failed')
            console.error(error)
        }
    }

    
    syncNow() {
        if ( this.syncConfig?.intervalMs > 0  ) {
            console.log('syncNow requested for ' + this.name)            
            let syncOpts = this.buildSyncOptions()
            this.rxCollection.syncGraphQL(syncOpts as SyncOptionsGraphQL)
        } else {
            console.warn(`syncNow ignored for rxdb collection "${this.name}" because it is already using a live sync`)
        }
    }

    // override this in specific collection to return different error wrapper
    wrapSyncError( e : any ) {
        return new TestbedRxdbGraphQlSyncError(e)
    }

}